// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Render lucide icons given html markup or icon names. 
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import {
    createElement,
    createIcons,
    AlertTriangle,
    ArrowBigRightDash,
    ArrowLeftRight,
    Asterisk,
    BadgeInfo,
    BadgeQuestionMark,
    Ban,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    CircleQuestionMark,
    ClipboardList,
    ClockAlert,
    Copy,
    Diff,
    Eye,
    Frown,
    GraduationCap,
    Hammer,
    Info,
    ListPlus,
    Lock,
    LogIn,
    LogOut,
    Mailbox,
    Menu,
    MousePointerClick,
    Package,
    PackageOpen,
    PartyPopper,
    Pencil,
    Plus,
    RefreshCcw,
    RotateCcw,
    Ruler,
    RulerDimensionLine,
    Search,
    Signpost,
    Scissors,
    ShoppingBasket,
    Sparkle,
    Sparkles,
    Star,
    Store,
    Toolbox,
    UserRound,
    UsersRound,
    Wrench,
    X
} from "lucide"

// @@body
const defaultIcons = {
    AlertTriangle,
    ArrowBigRightDash,
    ArrowLeftRight,
    Asterisk,
    BadgeInfo,
    BadgeQuestionMark,
    Ban,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    CircleQuestionMark,
    ClipboardList,
    ClockAlert,
    Copy,
    Diff,
    Eye,
    Frown,
    GraduationCap,
    Hammer,
    Info,
    ListPlus,
    Lock,
    LogIn,
    LogOut,
    Mailbox,
    Menu,
    MousePointerClick,
    Package,
    PackageOpen,
    PartyPopper,
    Pencil,
    Plus,
    RefreshCcw,
    RotateCcw,
    Ruler,
    RulerDimensionLine,
    Search,
    Signpost,
    Scissors,
    ShoppingBasket,
    Sparkle,
    Sparkles,
    Star,
    Store,
    Toolbox,
    UserRound,
    UsersRound,
    Wrench,
    X
}

/**
 * Render any icon tags in page with a data-lucide dataset attribute at time of
 * execution. Any matching icon tag will be replaced by a lucide svg element if
 * the given icon is found in the passed icons object.
 * @param {any} icons Object of lucide icons which are available to be used in
 * the page.
 */
const renderLucideIcons = (icons = defaultIcons) => { createIcons({ icons }) }

/**
 * Generate an SVG element from a given data-lucide icon name fetched from an
 * html icon (<i>) element.
 * @param {string} iconName Icon name in lower-kebab-case for which method
 * should return a corresponding SVG element.
 * @param {object} obj Optional destructured parameters object.
 * @param {any} [obj.icons] Object of lucide icons which are available to be
 * used in the page.
 * @returns {SVGElement?} Generated SVG element corresponding to the given name.
 */
const createLucideIcon = (iconName, { icons = defaultIcons } = {}) => {
    // Infer UpperPascalCase IconNode object name from lower-kebab-case html
    // data-lucide icon name. With the exception of a modified regexp, this
    // conversion uses the same resolution as the `toPascalCase` function found
    // [here](https://github.com/lucide-icons/lucide/blob/main/packages/lucide/src/replaceElement.ts).
    const componentName = iconName.replace(
        /(^\w|\w)(\w*)(-|$)/g,
        (_, /** @type {string} */ head, /** @type {string} */ tail) => {
            return `${head.toUpperCase()}${tail.toLowerCase()}`
        }
    )

    // Fetch IconNode object from the UpperPascalCase icon name.
    let component
    for (const key in icons) {
        if (key === componentName) {
            component = icons[key]
            break
        }
    }

    // Return generated SVG element if IconNode object found matching icon name.
    if (!component) { return null }
    return createElement(component)
}

// @@exports
export { renderLucideIcons, createLucideIcon }
